import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { Component, computed, effect, ElementRef, inject, signal, ViewChild, WritableSignal } from '@angular/core';
import {
	createDefaultGroupLoadStates,
	GroupLoadState,
	HEADER_SEARCH_CATEGORIES,
	HeaderSearchGroupResult,
	HeaderSearchQuery,
	HeaderSearchCategory,
	LOAD_MORE_SAFETY_CAP,
	SEARCH_MAX_CHARS,
	SEARCH_MIN_CHARS,
	SEARCH_PAGE_SIZE,
} from '../../../api/app-types/header-search.app';
import { HeaderSearchService } from '../../../api/services/header-search.service';
import { SearchService } from '../../../api/services/search.service';
import { Router } from '@angular/router';
import {
	PERSON_TYPE,
	personTypeTranslationKeys,
	SearchQuery,
	SearchResultItem,
	SearchScope,
	ToggleSearchScope,
} from '../../../api/app-types/search.app';
import { Search } from '../search/search';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { CUSTOM_DATE_FORMATS } from '../../adapters/custom-date-format';
import { DatePipe } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { LvrChip } from '../chip/chip';
import { REPRESENTATION_MODE } from '../../services/representation-mode.service';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { RECORD_MANAGER } from '../../services/record.provide';
import { LOGGER } from '../../../shared/logging/log.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ENVIRONMENT } from '../../../../environments/environment.interface';

interface GroupedSearchResultViewModel {
	key: HeaderSearchCategory | SearchScope;
	category?: HeaderSearchCategory;
	titleKey: string;
	items: SearchResultItem[];
	hasMore: boolean;
	isLoadingMore: boolean;
	loadMoreError: boolean;
	capReached: boolean;
}

interface LegacySearchGroupResult {
	scope: SearchScope;
	items: SearchResultItem[];
	hasMore: boolean;
	rawCount: number;
}

type SearchGroupResult = HeaderSearchGroupResult | LegacySearchGroupResult;

interface SearchState {
	term: string;
	groups: SearchGroupResult[];
	groupLoadStates: Record<HeaderSearchCategory, GroupLoadState>;
	isLoading: boolean;
	error: unknown;
}

interface HeaderSearchFilterOption {
	scope: HeaderSearchCategory | ToggleSearchScope;
	labelKey: string;
	checked: WritableSignal<boolean>;
}

@Component({
	selector: 'lvr-global-search',
	imports: [
		DatePipe,
		Search,
		CdkConnectedOverlay,
		CdkOverlayOrigin,
		TranslocoPipe,
		MatCheckboxModule,
		MatListModule,
		MatButtonModule,
		MatExpansionModule,
		MatIconModule,
		LvrChip,
		InfiniteScrollDirective,
	],
	templateUrl: './global-search.html',
	styleUrl: './global-search.scss',
	providers: [
		{
			provide: HeaderSearchService,
			useClass: HeaderSearchService,
		},
		{
			provide: SearchService,
			useClass: SearchService,
		},
	],
})
export class GlobalSearch {
	@ViewChild('origin', { static: true }) public origin!: CdkOverlayOrigin;
	@ViewChild('overlayContainer') private overlayContainer?: ElementRef<HTMLElement>;

	private readonly environment = inject(ENVIRONMENT);
	private readonly headerSearchApi = inject(HeaderSearchService);
	private readonly legacySearchApi = inject(SearchService);
	private readonly router = inject(Router);
	private readonly representationMode = inject(REPRESENTATION_MODE);
	private readonly transloco = inject(TranslocoService);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly logger = inject(LOGGER);
	private searchRequestId = 0; // Incremented on every new search so stale responses can be discarded
	private previousRepresentationKey?: string;
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	private readonly legacyGroupOrder: SearchScope[] = [
		'clients',
		'institutions',
		'sponsors',
		'users',
		'isos',
		'assignments',
	];

	public readonly searchState = signal<SearchState>({
		term: '',
		groups: [] as HeaderSearchGroupResult[],
		groupLoadStates: createDefaultGroupLoadStates(),
		isLoading: false,
		error: null as unknown,
	});
	public readonly searchTerm = computed(() => this.searchState().term);
	public get maxSearchLength(): number {
		return SEARCH_MAX_CHARS;
	}
	public readonly searchTermTooLong = () => this.searchState().term.length > SEARCH_MAX_CHARS;
	public readonly hasSearchError = computed(() => this.searchState().error !== null);
	public readonly isLoading = computed(() => this.searchState().isLoading);
	public readonly hasGroupedResults = computed(() => this.groupedResults().length > 0);
	public readonly panelOpen = signal<boolean>(false);
	public readonly overlayPositions: ConnectedPosition[] = [
		{
			originX: 'start', // Start from left edge of input
			originY: 'bottom', // Start from bottom edge of input
			overlayX: 'start', // Align left edge of overlay
			overlayY: 'top', // Align top edge of overlay
			offsetY: -8,
			offsetX: 0,
		},
	];

	public readonly searchInstitutions = signal<boolean>(false);
	public readonly searchInstitutionPersons = signal<boolean>(false);
	public readonly searchSponsors = signal<boolean>(false);
	public readonly searchIsos = signal<boolean>(false);
	public readonly searchAssignments = signal<boolean>(false);
	public readonly searchClients = signal<boolean>(true);
	public readonly searchUsers = signal<boolean>(false);
	public readonly searchClientEnvironments = signal<boolean>(false);

	public onTerm(term: string) {
		this.updateSearch(term);
		this.panelOpen.set(!!term?.trim());
	}

	public closePanel() {
		this.panelOpen.set(false);
	}

	public get PERSON_TYPE() {
		return PERSON_TYPE;
	}

	private readonly resetOnRepresentationContextChange = effect(() => {
		const currentRepresentationKey = this.getCurrentRepresentationKey();

		if (this.previousRepresentationKey === undefined) {
			this.previousRepresentationKey = currentRepresentationKey;
			return;
		}

		if (this.previousRepresentationKey !== currentRepresentationKey) {
			this.searchRequestId++;
			this.searchState.update((state) => ({
				...state,
				groups: [],
				groupLoadStates: createDefaultGroupLoadStates(),
				isLoading: false,
				error: null,
			}));
			this.panelOpen.set(false);
		}

		this.previousRepresentationKey = currentRepresentationKey;
	});

	public readonly filterOptions = computed<HeaderSearchFilterOption[]>(() =>
		this.environment.useHeaderSearch
			? [
					{ scope: HeaderSearchCategory.Clients, labelKey: 'global_search.clients', checked: this.searchClients },
					{
						scope: HeaderSearchCategory.Institutions,
						labelKey: 'global_search.institutions',
						checked: this.searchInstitutions,
					},
					{
						scope: HeaderSearchCategory.InstitutionPersons,
						labelKey: 'global_search.institution_persons',
						checked: this.searchInstitutionPersons,
					},
					{ scope: HeaderSearchCategory.Users, labelKey: 'global_search.users', checked: this.searchUsers },
					{ scope: HeaderSearchCategory.Sponsors, labelKey: 'global_search.sponsors', checked: this.searchSponsors },
					{
						scope: HeaderSearchCategory.IntegrationServiceOffices,
						labelKey: 'global_search.isos',
						checked: this.searchIsos,
					},
					{
						scope: HeaderSearchCategory.Assignments,
						labelKey: 'global_search.assignments',
						checked: this.searchAssignments,
					},
				]
			: [
					{ scope: 'clients', labelKey: 'global_search.clients', checked: this.searchClients },
					{ scope: 'institutions', labelKey: 'global_search.institutions', checked: this.searchInstitutions },
					{ scope: 'users', labelKey: 'global_search.users', checked: this.searchUsers },
					{ scope: 'sponsors', labelKey: 'global_search.sponsors', checked: this.searchSponsors },
					{ scope: 'isos', labelKey: 'global_search.isos', checked: this.searchIsos },
					{ scope: 'assignments', labelKey: 'global_search.assignments', checked: this.searchAssignments },
				],
	);

	public institutionTypeLabel(item: SearchResultItem) {
		switch (item.personType) {
			case PERSON_TYPE.INSTITUTION:
				return 'client.career.main_branch';
			case PERSON_TYPE.INSTITUTION_BRANCH:
				return 'personType.institution_branch';
			default:
				return '';
		}
	}

	public onOutsideClick(ev: MouseEvent) {
		const originEl = this.origin?.elementRef?.nativeElement as HTMLElement | null;
		// On click inside the input
		if (originEl && ev.target instanceof Node && originEl.contains(ev.target)) {
			return;
		}
		this.closePanel();
	}

	public toggleScope(filter: HeaderSearchFilterOption, checked: boolean) {
		filter.checked.set(checked);
		const term = this.searchTerm();
		if ((term?.trim().length ?? 0) >= SEARCH_MIN_CHARS) {
			this.updateSearch(term);
		}
	}

	public mapPersonTypeToKey(pt: PERSON_TYPE) {
		return personTypeTranslationKeys[pt] || 'unknown';
	}

	public selectResult(item: SearchResultItem) {
		this.panelOpen.set(false);
		switch (item.personType) {
			case PERSON_TYPE.INSTITUTION:
				this.router.navigate(['desk/institution', item.item?.id]);
				break;
			case PERSON_TYPE.INSTITUTION_PERSON:
				if (item.item.institutionId != null) {
					this.router.navigate(['desk/institution', item.item.institutionId, 'persons', item.item.id]);
				}
				break;
			case PERSON_TYPE.CLIENT:
				if (this.canActivateRepresentation(item)) {
					const representedUserId = this.getRepresentedUserId(item);

					// Switch to representation record manager context and close current tabs
					this.recordManager.switchToRepresentationContext(representedUserId as number);
					this.recordManager.closeAllRecords();

					this.representationMode.activate({
						id: representedUserId ? String(representedUserId) : undefined,
						name: this.getRepresentedUserName(item),
					});
				}
				this.router.navigate(['desk/client', item.item?.id]);
				break;
			case PERSON_TYPE.USER:
				this.router.navigate(['desk/user', item.item?.objectId]);
				break;
			case PERSON_TYPE.SPONSOR:
				this.router.navigate(['desk/sponsor', item.item?.id]);
				break;
			case PERSON_TYPE.INSTITUTION_BRANCH:
				this.router.navigate(['desk/institution', item.item.institutionId, 'workplace', item.item.id]);
				break;
			case PERSON_TYPE.INTEGRATION_SERVICE_OFFICE:
				this.router.navigate(['desk/integration-service-office', item.item.id]);
				break;
			case PERSON_TYPE.ASSIGNMENT: {
				const assignmentRoute = this.getAssignmentRoute(item);
				this.router.navigate(assignmentRoute);
				break;
			}
			default:
				console.warn('Unknown PersonType', item);
				break;
		}
	}

	public trackByResult = (_: number, r: SearchResultItem) => r.item?.id ?? r.item ?? r.personType;

	public canActivateRepresentation(item: SearchResultItem) {
		return item.personType === PERSON_TYPE.CLIENT && !!this.getRepresentedUserId(item);
	}

	private getAssignmentRoute(item: SearchResultItem) {
		const assignment = item.item;
		const route = ['/desk/client', assignment.clientId, 'assignments', assignment.id];
		const childRoute = assignment.isServiceOfficeStatement
			? 'fds'
			: assignment.isAdditionalAssignment
				? 'additional'
				: undefined;

		return childRoute ? [...route, childRoute] : route;
	}

	private getRepresentedUserId(item: SearchResultItem): string | number | undefined {
		const value = item.ableToRepresentAs;
		if (value == null) return undefined;
		return value;
	}

	private getRepresentedUserName(item: SearchResultItem): string | undefined {
		const userFirstName = item.item.user?.firstName?.trim();
		const userLastName = item.item.user?.name?.trim();
		const fullName = [userFirstName, userLastName].filter(Boolean).join(' ').trim();
		return fullName || undefined;
	}

	public getAssignmentOwnerName(item: SearchResultItem): string {
		const owner = item.item.ownerOfAdditionalAssignment ?? item.item.user;
		const fullName = [owner?.firstName?.trim(), owner?.name?.trim()].filter(Boolean).join(' ').trim();
		return fullName || '/';
	}

	public updateSearch(term: string): void {
		const trimmedTerm = term?.trim() ?? '';
		const isTermTooLong = term.length > SEARCH_MAX_CHARS;

		// Invalidate any in-flight request so its (possibly late) response is ignored.
		this.searchRequestId++;

		this.searchState.update((state) => ({
			...state,
			term,
			isLoading: trimmedTerm.length >= SEARCH_MIN_CHARS && !isTermTooLong,
			error: null,
			groupLoadStates: createDefaultGroupLoadStates(),
		}));

		if (trimmedTerm.length < SEARCH_MIN_CHARS || isTermTooLong) {
			this.searchState.update((state) => ({
				...state,
				groups: [],
				groupLoadStates: createDefaultGroupLoadStates(),
				isLoading: false,
			}));
			this.panelOpen.set(isTermTooLong);
			return;
		}

		void this.performSearch(term, this.searchRequestId);
	}

	/**
	 * Checks if the given date string is valid and different from the default DateOnly.MinValue ('0001-01-01'),
	 * which is returned by the backend when no date is provided.
	 * @param input - Date string to validate.
	 * @returns true if the date is valid and not the default min value.
	 */
	public isValidDate(input?: Date | string) {
		if (!input) {
			return false;
		}

		const date = new Date(input);
		if (Number.isNaN(date.getTime())) {
			return false;
		}

		// Check if it's the default '0001-01-01'
		const isMinDate = date.getFullYear() === 1 && date.getMonth() === 0 && date.getDate() === 1;

		return !isMinDate;
	}

	/**
	 * Filters and orders the groups returned by the header-search endpoint.
	 */
	public readonly groupedResults = computed<GroupedSearchResultViewModel[]>(() => {
		const orderedGroups: GroupedSearchResultViewModel[] = [];
		const headerGroups = new Map<HeaderSearchCategory, HeaderSearchGroupResult>();
		const legacyGroups = new Map<SearchScope, LegacySearchGroupResult>();

		for (const group of this.searchState().groups) {
			if ('category' in group) {
				headerGroups.set(group.category, group);
			} else {
				legacyGroups.set(group.scope, group);
			}
		}

		if (this.environment.useHeaderSearch) {
			for (const category of HEADER_SEARCH_CATEGORIES) {
				if (!this.isHeaderCategoryEnabled(category)) continue;

				const group = headerGroups.get(category);
				const loadState = this.searchState().groupLoadStates[category];
				if (!group?.items.length) continue;

				orderedGroups.push({
					key: category,
					category,
					titleKey: this.translateHeaderCategory(category),
					items: group.items,
					hasMore: group.hasMore,
					isLoadingMore: loadState.isLoadingMore,
					loadMoreError: loadState.loadMoreError,
					capReached: loadState.pagesLoaded >= LOAD_MORE_SAFETY_CAP,
				});
			}
		} else {
			for (const scope of this.legacyGroupOrder) {
				if (!this.isScopeEnabled(scope)) continue;

				const group = legacyGroups.get(scope);
				if (!group?.items.length) continue;

				orderedGroups.push({
					key: scope,
					titleKey: this.translateGroupKey(scope),
					items: group.items,
					hasMore: false,
					isLoadingMore: false,
					loadMoreError: false,
					capReached: false,
				});
			}
		}

		return orderedGroups;
	});

	private isHeaderSearchGroup(group: SearchGroupResult): group is HeaderSearchGroupResult {
		return 'category' in group;
	}

	public async loadMore(category: HeaderSearchCategory): Promise<void> {
		if (!this.environment.useHeaderSearch) {
			return;
		}

		const currentState = this.searchState();
		const group = currentState.groups.find(
			(candidate): candidate is HeaderSearchGroupResult =>
				this.isHeaderSearchGroup(candidate) && candidate.category === category,
		);
		if (!group) {
			return;
		}

		const loadState = currentState.groupLoadStates[category];
		if (
			!group.hasMore ||
			loadState.isLoadingMore ||
			loadState.loadMoreError ||
			loadState.pagesLoaded >= LOAD_MORE_SAFETY_CAP
		) {
			return;
		}

		const capturedGeneration = this.searchRequestId;
		const capturedRepresentationKey = this.getCurrentRepresentationKey();
		const backendOffset = loadState.backendOffset;

		this.updateGroupLoadState(category, (state) => ({
			...state,
			isLoadingMore: true,
		}));

		try {
			const response = await this.headerSearchApi.search(this.buildLoadMoreQuery(category, backendOffset));

			if (!this.isResponseStillValid(capturedGeneration, capturedRepresentationKey)) {
				return;
			}

			const responseGroup = response.groups.find((candidate) => candidate.category === category);
			if (!responseGroup) {
				this.setGroupHasMore(category, false);
				return;
			}

			const rawCount = responseGroup.rawCount ?? responseGroup.items.length;
			if (rawCount === 0) {
				if (responseGroup.hasMore) {
					this.logger.warn('GlobalSearch loadMore returned no results while hasMore=true', {
						category,
						backendOffset,
					});
				}
				this.setGroupHasMore(category, false);
				return;
			}

			const nextItems = this.normalizeGroupItems(responseGroup.items ?? []);
			this.searchState.update((state) => {
				const currentGroup = state.groups.find(
					(candidate): candidate is HeaderSearchGroupResult =>
						this.isHeaderSearchGroup(candidate) && candidate.category === category,
				);
				if (!currentGroup) {
					return state;
				}

				const mergedItems = this.deduplicateItems([...currentGroup.items, ...nextItems]);
				const groupLoadState = state.groupLoadStates[category];

				return {
					...state,
					groups: state.groups.map((candidate) =>
						this.isHeaderSearchGroup(candidate) && candidate.category === category
							? {
									...candidate,
									items: mergedItems,
									hasMore: responseGroup.hasMore,
								}
							: candidate,
					),
					groupLoadStates: {
						...state.groupLoadStates,
						[category]: {
							...groupLoadState,
							backendOffset: groupLoadState.backendOffset + rawCount,
							pagesLoaded: groupLoadState.pagesLoaded + 1,
							loadMoreError: false,
						},
					},
				};
			});
		} catch (error) {
			if (!this.isResponseStillValid(capturedGeneration, capturedRepresentationKey)) {
				return;
			}

			this.logger.warn('GlobalSearch loadMore failed', {
				category,
				statusClass: this.resolveHttpStatusClass(error),
				backendOffset,
			});

			this.updateGroupLoadState(category, (state) => ({
				...state,
				loadMoreError: true,
			}));
		} finally {
			if (this.isResponseStillValid(capturedGeneration, capturedRepresentationKey)) {
				this.updateGroupLoadState(category, (state) => ({
					...state,
					isLoadingMore: false,
				}));
			}
		}
	}

	public retryLoadMore(category: HeaderSearchCategory): void {
		this.updateGroupLoadState(category, (state) => ({
			...state,
			loadMoreError: false,
		}));
		void this.loadMore(category);
	}

	private async performSearch(term: string, requestId: number): Promise<void> {
		const capturedRepresentationKey = this.getCurrentRepresentationKey();

		try {
			let groups: SearchGroupResult[];
			let groupLoadStates = createDefaultGroupLoadStates();

			if (this.environment.useHeaderSearch) {
				const response = await this.headerSearchApi.search(this.buildHeaderSearchQuery(term));
				groups = response.groups
					.map((group) => ({
						...group,
						items: this.normalizeGroupItems(group.items ?? []),
						rawCount: group.rawCount ?? group.items.length,
					}))
					.filter((group) => group.items.length > 0);
				groupLoadStates = this.buildGroupLoadStates(
					groups.filter((group): group is HeaderSearchGroupResult => this.isHeaderSearchGroup(group)),
				);
			} else {
				const response = await this.legacySearchApi.search(this.buildLegacySearchQuery(term));
				groups = this.toLegacySearchGroups(response.entities);
			}

			if (!this.isResponseStillValid(requestId, capturedRepresentationKey)) return;

			this.searchState.update((state) => ({
				...state,
				groups,
				groupLoadStates,
				isLoading: false,
			}));

			this.panelOpen.set(!!term.trim());
		} catch (error) {
			if (!this.isResponseStillValid(requestId, capturedRepresentationKey)) return;

			this.logger.warn('GlobalSearch search failed', {
				statusClass: this.resolveHttpStatusClass(error),
			});
			this.searchState.update((state) => ({
				...state,
				groups: [],
				groupLoadStates: createDefaultGroupLoadStates(),
				isLoading: false,
				error,
			}));
			this.panelOpen.set(!!term.trim());
		}
	}

	private buildHeaderSearchQuery(term: string): HeaderSearchQuery {
		return {
			searchTerm: term,
			categories: HEADER_SEARCH_CATEGORIES.filter((category) => this.isHeaderCategoryEnabled(category)),
			limitPerCategory: SEARCH_PAGE_SIZE,
		};
	}

	private buildLegacySearchQuery(term: string): SearchQuery {
		return {
			searchTerm: term,
			searchClients: this.searchClients(),
			searchUsers: this.searchUsers(),
			searchClientEnvironments: false,
			searchInstitutions: this.searchInstitutions(),
			searchInstitutionBranches: this.searchInstitutions(),
			searchInstitutionPerson: false,
			searchSponsors: this.searchSponsors(),
			searchIso: this.searchIsos(),
			searchAssignments: this.searchAssignments(),
		};
	}

	private toLegacySearchGroups(items: SearchResultItem[]): LegacySearchGroupResult[] {
		const groupedItems = new Map<SearchScope, SearchResultItem[]>();

		for (const item of items ?? []) {
			const scope = this.getLegacyScope(item);
			if (!scope || !this.isScopeEnabled(scope)) {
				continue;
			}

			const scopeItems = groupedItems.get(scope) ?? [];
			scopeItems.push(item);
			groupedItems.set(scope, scopeItems);
		}

		return this.legacyGroupOrder.flatMap((scope) => {
			const scopeItems = groupedItems.get(scope) ?? [];
			if (scopeItems.length === 0) {
				return [];
			}

			return [
				{
					scope,
					items: scopeItems,
					hasMore: false,
					rawCount: scopeItems.length,
				},
			];
		});
	}

	private getLegacyScope(item: SearchResultItem): SearchScope | undefined {
		switch (item.personType) {
			case PERSON_TYPE.CLIENT:
				return 'clients';
			case PERSON_TYPE.INSTITUTION:
			case PERSON_TYPE.INSTITUTION_BRANCH:
				return 'institutions';
			case PERSON_TYPE.USER:
				return 'users';
			case PERSON_TYPE.SPONSOR:
				return 'sponsors';
			case PERSON_TYPE.INTEGRATION_SERVICE_OFFICE:
				return 'isos';
			case PERSON_TYPE.ASSIGNMENT:
				return 'assignments';
			default:
				return undefined;
		}
	}

	private isScopeEnabled(scope: SearchScope): boolean {
		switch (scope) {
			case 'clients':
				return this.searchClients();
			case 'institutions':
				return this.searchInstitutions();
			case 'users':
				return this.searchUsers();
			case 'sponsors':
				return this.searchSponsors();
			case 'isos':
				return this.searchIsos();
			case 'assignments':
				return this.searchAssignments();
		}
	}

	private isHeaderCategoryEnabled(category: HeaderSearchCategory): boolean {
		switch (category) {
			case HeaderSearchCategory.Clients:
				return this.searchClients();
			case HeaderSearchCategory.Institutions:
				return this.searchInstitutions();
			case HeaderSearchCategory.InstitutionPersons:
				return this.searchInstitutionPersons();
			case HeaderSearchCategory.Users:
				return this.searchUsers();
			case HeaderSearchCategory.Sponsors:
				return this.searchSponsors();
			case HeaderSearchCategory.IntegrationServiceOffices:
				return this.searchIsos();
			case HeaderSearchCategory.Assignments:
				return this.searchAssignments();
		}
	}

	private translateHeaderCategory(category: HeaderSearchCategory): string {
		switch (category) {
			case HeaderSearchCategory.Clients:
				return 'global_search.clients';
			case HeaderSearchCategory.Institutions:
				return 'global_search.institutions';
			case HeaderSearchCategory.InstitutionPersons:
				return 'global_search.institution_persons';
			case HeaderSearchCategory.Users:
				return 'global_search.users';
			case HeaderSearchCategory.Sponsors:
				return 'global_search.sponsors';
			case HeaderSearchCategory.IntegrationServiceOffices:
				return 'global_search.isos';
			case HeaderSearchCategory.Assignments:
				return 'global_search.assignments';
		}
	}

	private buildLoadMoreQuery(category: HeaderSearchCategory, offset: number): HeaderSearchQuery {
		return {
			searchTerm: this.searchTerm(),
			categories: [category],
			limitPerCategory: SEARCH_PAGE_SIZE,
			offset,
		};
	}

	private buildGroupLoadStates(groups: HeaderSearchGroupResult[]): Record<HeaderSearchCategory, GroupLoadState> {
		const states = createDefaultGroupLoadStates();
		for (const group of groups) {
			states[group.category] = {
				backendOffset: group.rawCount ?? group.items.length,
				isLoadingMore: false,
				loadMoreError: false,
				pagesLoaded: 0,
			};
		}
		return states;
	}

	private updateGroupLoadState(
		category: HeaderSearchCategory,
		updater: (state: GroupLoadState) => GroupLoadState,
	): void {
		this.searchState.update((state) => ({
			...state,
			groupLoadStates: {
				...state.groupLoadStates,
				[category]: updater(state.groupLoadStates[category]),
			},
		}));
	}

	private setGroupHasMore(category: HeaderSearchCategory, hasMore: boolean): void {
		this.searchState.update((state) => ({
			...state,
			groups: state.groups.map((candidate) =>
				this.isHeaderSearchGroup(candidate) && candidate.category === category ? { ...candidate, hasMore } : candidate,
			),
		}));
	}

	private normalizeGroupItems(items: SearchResultItem[]): SearchResultItem[] {
		return this.deduplicateItems(
			items
				.filter((item) => this.hasRenderableItem(item))
				.filter((item) => !(this.isInstitutionBranch(item) && item.item.isMainInstitution)),
		);
	}

	private deduplicateItems(items: SearchResultItem[]): SearchResultItem[] {
		const seen = new Set<string>();
		return items.filter((item) => {
			const key = this.toResultIdentity(item);
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
	}

	private toResultIdentity(item: SearchResultItem): string {
		if (item.item.objectId) {
			return `object:${item.item.objectId}`;
		}
		return `id:${item.item.id}`;
	}

	private isResponseStillValid(requestId: number, capturedRepresentationKey: string): boolean {
		return requestId === this.searchRequestId && capturedRepresentationKey === this.getCurrentRepresentationKey();
	}

	private getCurrentRepresentationKey(): string {
		const mode = this.representationMode.mode();
		return mode.active ? `represented:${mode.representedUserId ?? ''}` : 'default';
	}

	private resolveHttpStatusClass(error: unknown): string {
		const status = typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;
		if (typeof status !== 'number') {
			return 'unknown';
		}
		return `${Math.floor(status / 100)}xx`;
	}

	private hasRenderableItem(item: SearchResultItem | null | undefined): item is SearchResultItem {
		if (!item || !item.item) return false;
		return item.item.id != null || !!item.item.objectId;
	}

	private isInstitutionBranch(item: SearchResultItem) {
		return item.personType === PERSON_TYPE.INSTITUTION_BRANCH || !!item.item.institutionId;
	}

	private translateGroupKey(scope: string) {
		switch (scope) {
			case 'clients':
				return 'global_search.clients';
			case 'institutions':
				return 'global_search.institutions';
			case 'users':
				return 'global_search.users';
			case 'sponsors':
				return 'global_search.sponsors';
			case 'isos':
				return 'global_search.isos';
			case 'assignments':
				return 'global_search.assignments';
			default:
				return 'global_search.unknown';
		}
	}

	private mapPhaseIdToPhase(phaseId: number) {
		switch (phaseId) {
			case DB_PHASES.P0:
				return 'p0';
			case DB_PHASES.P1:
				return 'p1';
			case DB_PHASES.P2:
				return 'p2';
			case DB_PHASES.P3:
				return 'p3';
			case DB_PHASES.P4:
				return 'p4';
			case DB_PHASES.FP3:
				return 'fp3';
			case DB_PHASES.FP4:
				return 'fp4';
			case DB_PHASES.FP5:
				return 'fp5';
			default:
				return 'unknown';
		}
	}

	public getAssignmentTitle(item: SearchResultItem) {
		if (item.personType !== PERSON_TYPE.ASSIGNMENT) return;
		const assignment = item.item;
		const isDraft = assignment.phaseId === DB_PHASES.P1 && assignment.draftPhaseId != null;
		const careType = assignment.categoryTypeOfCare?.initial ?? assignment.categoryTypeOfCare?.remark ?? '-';

		if (assignment.isServiceOfficeStatement || assignment.phaseId! >= DB_PHASES.FP3) {
			switch (assignment.phaseId) {
				case DB_PHASES.FP3:
					return { key: 'assignment.title.fds_fp3' };
				case DB_PHASES.FP4:
					return { key: 'assignment.title.fds_fp4' };
				case DB_PHASES.FP5:
					return { key: 'assignment.title.fds_fp5' };
				default:
					return { key: 'assignment.fds.title' };
			}
		}

		if (assignment.isAdditionalAssignment) {
			switch (assignment.phaseId) {
				case DB_PHASES.P1:
					return { key: 'assignment.title.additional_draft', params: { careType } };
				case DB_PHASES.P3:
					return { key: 'assignment.title.additional_p3', params: { careType } };
				case DB_PHASES.P4:
					return { key: 'assignment.title.additional_p4', params: { careType } };
			}
		}

		if (isDraft) {
			switch (assignment.draftPhaseId) {
				case DB_PHASES.P2:
					return { key: 'assignment.title.main_draft_p2' };
				case DB_PHASES.P3:
					return { key: 'assignment.title.main_draft_p3', params: { careType } };
			}
		}

		switch (assignment.phaseId) {
			case DB_PHASES.P2:
				return { key: 'assignment.title.main_p2' };

			case DB_PHASES.P3:
				return { key: 'assignment.title.main_p3', params: { careType } };

			case DB_PHASES.P4:
				return careType !== '-'
					? { key: 'assignment.title.main_p4', params: { careType } }
					: { key: 'assignment.title.main_p4_no_care_type' };

			default:
				return { key: `base.phase.${this.mapPhaseIdToPhase(assignment.phaseId!)}` };
		}
	}

	// Accessibility methods for keyboard navigation and screen readers
	public closePanelAndRestoreFocus() {
		this.closePanel();
		this.focusSearchInput();
	}

	public focusFirstOverlayElement(event: Event) {
		if (!this.panelOpen()) {
			return;
		}

		event.preventDefault();

		queueMicrotask(() => {
			this.getFocusableOverlayElements()[0]?.focus();
		});
	}

	public focusNextResult(event: Event) {
		event.preventDefault();

		const elements = this.getFocusableOverlayElements();

		if (!elements.length) {
			return;
		}

		const currentIndex = elements.indexOf(document.activeElement as HTMLElement);
		const nextIndex = currentIndex >= 0 ? currentIndex + 1 : 0;

		elements[nextIndex % elements.length]?.focus();
	}

	public focusPreviousResult(event: Event) {
		event.preventDefault();

		const elements = this.getFocusableOverlayElements();

		if (!elements.length) {
			return;
		}

		const currentIndex = elements.indexOf(document.activeElement as HTMLElement);
		const previousIndex = currentIndex > 0 ? currentIndex - 1 : elements.length - 1;

		elements[previousIndex]?.focus();
	}

	public handleOverlayTab(event: Event) {
		const keyboardEvent = event as KeyboardEvent;
		const elements = this.getFocusableOverlayElements();

		if (!elements.length) {
			return;
		}

		const firstElement = elements[0];
		const lastElement = elements[elements.length - 1];

		if (keyboardEvent.shiftKey && document.activeElement === firstElement) {
			event.preventDefault();
			this.focusSearchInput();
			return;
		}

		if (!keyboardEvent.shiftKey && document.activeElement === lastElement) {
			event.preventDefault();
			this.closePanelAndRestoreFocus();
		}
	}

	private getFocusableOverlayElements() {
		return Array.from(
			this.overlayContainer?.nativeElement.querySelectorAll<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			) ?? [],
		).filter((element) => !element.hasAttribute('disabled'));
	}

	private focusSearchInput() {
		this.origin.elementRef.nativeElement.querySelector('input')?.focus();
	}

	public focusFirstResult(event: Event) {
		if (!this.panelOpen()) {
			return;
		}

		event.preventDefault();

		queueMicrotask(() => {
			this.getResultItems()[0]?.focus();
		});
	}

	private getResultItems() {
		return Array.from(this.overlayContainer?.nativeElement.querySelectorAll<HTMLElement>('.result-item') ?? []);
	}

	private getClientResultAriaLabel(item: SearchResultItem) {
		const name = `${item.item.name}, ${item.item.firstName ?? ''}`.trim();
		const owner = item.item.user?.name ?? '/';
		const birthDate = this.isValidDate(item.item.dateOfBirth) ? item.item.dateOfBirth : undefined;

		const chips = [
			item.item.archiveId || item.item.isArchived ? this.transloco.translate('global_search.archive') : undefined,
			this.canActivateRepresentation(item) ? this.transloco.translate('global_search.representation') : undefined,
		]
			.filter(Boolean)
			.join(', ');

		return [name, birthDate, owner, chips].filter(Boolean).join('. ');
	}

	public getResultAriaLabel(item: SearchResultItem) {
		switch (item.personType) {
			case PERSON_TYPE.CLIENT:
				return this.getClientResultAriaLabel(item);

			case PERSON_TYPE.USER:
				return `${item.item.name}, ${item.item.firstName}. ${item.item.integrationServiceOffice?.remark ?? ''}`;

			case PERSON_TYPE.ASSIGNMENT: {
				const title = this.getAssignmentTitle(item);

				return title
					? `${title.key}. ${item.item.guid ?? ''}`
					: `${this.mapPersonTypeToKey(item.personType)}. ${item.item.guid ?? ''}`;
			}

			case PERSON_TYPE.INSTITUTION:
			case PERSON_TYPE.INSTITUTION_BRANCH:
			case PERSON_TYPE.SPONSOR:
			case PERSON_TYPE.INTEGRATION_SERVICE_OFFICE:
				return `${item.item.name ?? item.item.remark}. ${this.institutionTypeLabel(item)}`;

			default:
				return `${item.item.name ?? ''}. ${this.mapPersonTypeToKey(item.personType)}`;
		}
	}
}
